import { useMemo } from "react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { EVENT_TYPES, scrapingChartConfig, PluginRow } from "@/lib/scrapingEvents";
import { WorkerInfo } from "@/services/scrapingStatsApi";

// Libellé court et unique par worker (le nom peut être partagé entre workers,
// d'où le suffixe d'id pour éviter la fusion de catégories recharts).
function workerLabel(workerId: string, info?: WorkerInfo): string {
  const short = workerId.slice(0, 4);
  if (info?.is_anonymous) return `Anonyme · ${short}`;
  if (info?.user_name) return `${info.user_name} · ${short}`;
  return workerId.slice(0, 10);
}

// Répartition de la charge (volume de tâches sur la période) par worker, en
// barres horizontales empilées par type d'évènement. Top N + nœud « Autres ».
export function WorkerLoadBarChart({
  rows,
  workersMap,
  loading,
  topN = 12,
}: {
  rows: PluginRow[];
  workersMap: Record<string, WorkerInfo>;
  loading?: boolean;
  topN?: number;
}) {
  const data = useMemo(() => {
    const top = rows.slice(0, topN).map((row) => {
      const point: Record<string, number | string> = {
        label: workerLabel(row.worker_id, workersMap[row.worker_id]),
      };
      for (const t of EVENT_TYPES) point[t] = row[t] || 0;
      return point;
    });
    const rest = rows.slice(topN);
    if (rest.length > 0) {
      const others: Record<string, number | string> = { label: `Autres (${rest.length})` };
      for (const t of EVENT_TYPES) {
        others[t] = rest.reduce((acc, r) => acc + (r[t] || 0), 0);
      }
      top.push(others);
    }
    return top;
  }, [rows, workersMap, topN]);

  if (loading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Chargement...</div>;
  }
  if (data.length === 0) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Aucune donnée</div>;
  }

  return (
    <ChartContainer
      config={scrapingChartConfig}
      className="w-full"
      style={{ height: Math.max(240, data.length * 34 + 40) }}
    >
      <BarChart data={data} layout="vertical" margin={{ left: 12, right: 16 }}>
        <CartesianGrid horizontal={false} />
        <XAxis type="number" tickLine={false} axisLine={false} allowDecimals={false} />
        <YAxis type="category" dataKey="label" tickLine={false} axisLine={false} width={170} />
        <ChartTooltip content={<ChartTooltipContent />} />
        {EVENT_TYPES.map((type, idx) => (
          <Bar
            key={type}
            dataKey={type}
            stackId="a"
            fill={`var(--color-${type})`}
            radius={
              idx === 0
                ? [4, 0, 0, 4]
                : idx === EVENT_TYPES.length - 1
                ? [0, 4, 4, 0]
                : [0, 0, 0, 0]
            }
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}
